/**
 * Dashboard State Hook
 * 
 * Central state management hook for the Dashboard component.
 * Uses React Query for server state and a simplified reducer for UI state.
 */

import { useCallback, useEffect } from "react";

import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { useOnboardingTracking } from "@/hooks/useOnboardingTracking";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/lib/routes";
import { useReducerWithDevtools } from "@/lib/useReducerWithDevtools";
import { dashboardReducer, initialDashboardState, dashboardActions } from "@/reducers/dashboardReducer";

import type { Workspace, Board, Project, Folder, Tag } from "@/types/state";

// --- React Query Keys ---
const dashboardKeys = {
  all: ['dashboard'] as const,
  workspace: (userId: string) => [...dashboardKeys.all, 'workspace', userId] as const,
  boards: (workspaceId: string) => [...dashboardKeys.all, 'boards', workspaceId] as const,
  projects: (workspaceId: string) => [...dashboardKeys.all, 'projects', workspaceId] as const,
  folders: (workspaceId: string) => [...dashboardKeys.all, 'folders', workspaceId] as const,
  tags: (workspaceId: string) => [...dashboardKeys.all, 'tags', workspaceId] as const,
};

// --- Data Fetching Functions ---

const fetchWorkspace = async (userId: string): Promise<Workspace | null> => {
  const { data: workspaces } = await supabase
    .from("workspaces")
    .select("*")
    .eq("user_id", userId);

  if (!workspaces || workspaces.length === 0) {
    const { data: newWorkspace } = await supabase
      .from("workspaces")
      .insert({ user_id: userId, name: "My Workspace" })
      .select()
      .single();
    return newWorkspace;
  }
  return workspaces[0];
};

const fetchBoards = async (workspaceId: string): Promise<Board[]> => {
  const { data } = await supabase
    .from("boards")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false });
  return data || [];
};

const fetchProjects = async (boardIds: string[]): Promise<Project[]> => {
  if (boardIds.length === 0) return [];
  const { data } = await supabase
    .from("projects")
    .select("*, boards(name, color)")
    .in("board_id", boardIds)
    .eq("archived", false)
    .order("created_at", { ascending: false });
  return data || [];
};

const fetchFolders = async (workspaceId: string): Promise<Folder[]> => {
  const { data } = await supabase
    .from("folders")
    .select("id, name, parent_folder_id, depth")
    .eq("workspace_id", workspaceId)
    .order("name");
  return data || [];
};

const fetchTags = async (workspaceId: string): Promise<Tag[]> => {
  const { data } = await supabase
    .from("tags")
    .select("id, name, color")
    .eq("workspace_id", workspaceId)
    .order("name");
  return data || [];
};

// --- Main Hook ---

export function useDashboardState() {
  const { user, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { execute: invoke } = useEdgeFunction({ auth: true });
  const { trackEvent } = useOnboardingTracking();
  const queryClient = useQueryClient();
  
  const [state, dispatch] = useReducerWithDevtools(
    dashboardReducer,
    initialDashboardState,
    'Dashboard'
  );

  // --- React Query Hooks for Data Fetching ---

  const { data: workspace, isLoading: isLoadingWorkspace } = useQuery({
    queryKey: dashboardKeys.workspace(user?.id || ''),
    queryFn: () => fetchWorkspace(user!.id),
    enabled: !!user,
  });
  const workspaceId = workspace?.id;

  const { data: boards = [], isLoading: isLoadingBoards } = useQuery({
    queryKey: dashboardKeys.boards(workspaceId || ''),
    queryFn: () => fetchBoards(workspaceId!),
    enabled: !!workspaceId,
  });

  const boardIds = boards.map(b => b.id);
  const { data: projects = [], isLoading: isLoadingProjects } = useQuery({
    queryKey: dashboardKeys.projects(workspaceId || ''),
    queryFn: () => fetchProjects(boardIds),
    enabled: !!workspaceId && boardIds.length > 0,
  });

  const { data: folders = [], isLoading: isLoadingFolders } = useQuery({
    queryKey: dashboardKeys.folders(workspaceId || ''),
    queryFn: () => fetchFolders(workspaceId!),
    enabled: !!workspaceId,
  });

  const { data: tags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: dashboardKeys.tags(workspaceId || ''),
    queryFn: () => fetchTags(workspaceId!),
    enabled: !!workspaceId,
  });

  const loading = isLoadingWorkspace || isLoadingBoards || isLoadingProjects || isLoadingFolders || isLoadingTags;

  // --- Mutation for Creating a Project ---

  const createProjectMutation = useMutation({
    mutationFn: async (newProject: { name: string; board_id: string; folder_id: string | null; tags: string[] }) => {
      const { data: projectData, error } = await supabase
        .from("projects")
        .insert({
          name: newProject.name,
          board_id: newProject.board_id,
          folder_id: newProject.folder_id,
        })
        .select()
        .single();

      if (error) throw error;

      if (newProject.tags.length > 0) {
        const tagRelations = newProject.tags.map(tagId => ({
          project_id: projectData.id,
          tag_id: tagId,
        }));
        const { error: tagError } = await supabase.from("project_tags").insert(tagRelations);
        if (tagError) {
          console.error("Failed to associate tags:", tagError);
          toast.warning("Project created, but failed to add tags.");
        }
      }
      return projectData;
    },
    onSuccess: (newProject) => {
      toast.success("Project created successfully");
      queryClient.invalidateQueries({ queryKey: dashboardKeys.projects(workspaceId || '') });
      dispatch(dashboardActions.setCreateProjectDialogOpen(false));
      navigate(`/projects/${newProject.id}`);
    },
    onError: (error) => {
      console.error("Failed to create project:", error);
      toast.error("Failed to create project");
    },
  });

  // --- Callbacks and Event Handlers ---

  const fetchVerificationStatus = useCallback(async () => {
    if (!user) return;
    try {
      const { data: profile } = await supabase.from("profiles").select("email_verified").eq("id", user.id).single();
      dispatch(dashboardActions.setEmailVerified(profile?.email_verified ?? true));
    } catch (error) {
      console.error("Failed to fetch verification status:", error);
    }
  }, [user]);

  const handleResendVerification = useCallback(async () => {
    if (!user || !session) return;
    const confirmationUrl = `${window.location.origin}/verify-email?type=signup&token_hash=${session.access_token}`;
    
    const { error } = await invoke("send-verification-email", {
      body: { email: user.email, confirmationUrl },
      showErrorToast: true,
    });

    if (!error) {
      toast.success("Verification email sent! Please check your inbox.");
    }
  }, [user, session, invoke]);

  const refetchDashboardData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
  }, [queryClient]);

  const handleCreateBoard = useCallback(() => navigate(ROUTES.BOARDS_CREATE), [navigate]);
  const handleCreateProject = useCallback(() => {
    if (boards.length === 0) {
      toast.error("Please create a board first before adding projects");
      return;
    }
    dispatch(dashboardActions.setCreateProjectDialogOpen(true));
  }, [boards]);

  const handleSearch = useCallback(() => {
    if (!state.searchQuery.trim()) return;
    navigate(`/search?q=${encodeURIComponent(state.searchQuery)}`);
  }, [state.searchQuery, navigate]);

  const handleSearchQueryChange = useCallback((query: string) => dispatch(dashboardActions.setSearchQuery(query)), []);
  const handleToggleProjects = useCallback((open: boolean) => dispatch(dashboardActions.setProjectsOpen(open)), []);
  const handleToggleBoards = useCallback((open: boolean) => dispatch(dashboardActions.setBoardsOpen(open)), []);
  const handleToggleActivity = useCallback((open: boolean) => dispatch(dashboardActions.setActivityOpen(open)), []);
  const handleSetCreateProjectDialogOpen = useCallback((open: boolean) => dispatch(dashboardActions.setCreateProjectDialogOpen(open)), []);
  const handleProjectClick = useCallback((projectId: string) => navigate(`/projects/${projectId}`), [navigate]);
  const handleBoardClick = useCallback((boardId: string) => navigate(`/boards/${boardId}`), [navigate]);

  // --- Effects ---

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(ROUTES.AUTH);
      return;
    }
    fetchVerificationStatus();
  }, [user, authLoading, navigate, fetchVerificationStatus]);

  useEffect(() => {
    const skipRedirect = sessionStorage.getItem('skipDashboardRedirect');
    if (!loading && !skipRedirect && boards.length === 0 && projects.length === 0) {
      trackEvent("auto_redirect");
      navigate(ROUTES.BOARDS_CREATE, { replace: true });
    }
  }, [loading, boards, projects, navigate, trackEvent]);

  return {
    // State from reducer
    ...state,
    // Data from React Query
    workspace,
    boards,
    projects,
    folders,
    tags,
    loading,
    // Mutations
    createProject: createProjectMutation,
    // Handlers
    refetchDashboardData,
    handleResendVerification,
    handleCreateBoard,
    handleCreateProject,
    handleSearch,
    handleSearchQueryChange,
    handleToggleProjects,
    handleToggleBoards,
    handleToggleActivity,
    handleSetCreateProjectDialogOpen,
    handleProjectClick,
    handleBoardClick,
  };
}
